import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Typography, Box, Stack } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import colors from "config/theme/colors";
import { useNavigate } from "react-router";
import { useCustomNavigation } from "components/providers/NavigationProvider";

const ExitFormModal = ({ isFormDirty }) => {
  const [showExitModal, setShowExitModal] = useState(false);
  const [navigationConfirmed, setNavigationConfirmed] = useState(false);
  const { prevLocation } = useCustomNavigation();
  const navigate = useNavigate();

  const isDirtyRef = useRef(isFormDirty);
  const navigationConfirmedRef = useRef(navigationConfirmed);
  const hasHistoryTrapRef = useRef(false);
  const ignoreNextPopStateRef = useRef(false);

  useEffect(() => {
    isDirtyRef.current = isFormDirty;
  }, [isFormDirty]);

  useEffect(() => {
    navigationConfirmedRef.current = navigationConfirmed;
  }, [navigationConfirmed]);

  const pushHistoryTrap = () => {
    window.history.pushState(
      {
        ...(window.history.state || {}),
        exitFormModalTrap: true,
      },
      "",
      window.location.href,
    );
    hasHistoryTrapRef.current = true;
  };

  const handlePopState = () => {
    if (ignoreNextPopStateRef.current) {
      ignoreNextPopStateRef.current = false;
      return;
    }

    if (isDirtyRef.current && !navigationConfirmedRef.current) {
      setShowExitModal(true);
      pushHistoryTrap();
    }
  };

  const handleBeforeUnload = (e) => {
    if (isDirtyRef.current && !navigationConfirmedRef.current) {
      e.preventDefault();
      e.returnValue = "";
    }
  };

  useEffect(() => {
    if (isFormDirty && !hasHistoryTrapRef.current) {
      pushHistoryTrap();
    }

    if (!isFormDirty && hasHistoryTrapRef.current) {
      hasHistoryTrapRef.current = false;
      ignoreNextPopStateRef.current = true;
      window.history.back();
    }

    if (!isFormDirty) {
      setShowExitModal(false);
      navigationConfirmedRef.current = false;
      setNavigationConfirmed(false);
    }
  }, [isFormDirty]);

  useEffect(() => {
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      hasHistoryTrapRef.current = false;
      ignoreNextPopStateRef.current = false;
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handleConfirmNavigation = () => {
    navigationConfirmedRef.current = true;
    setNavigationConfirmed(true);
    setShowExitModal(false);

    if (hasHistoryTrapRef.current) {
      hasHistoryTrapRef.current = false;
      ignoreNextPopStateRef.current = true;
      navigate(-2);
    } else if (prevLocation?.pathname) {
      const { pathname } = prevLocation;
      navigate(pathname);
    } else {
      navigate(-1);
    }
  };

  const handleCancelNavigation = () => {
    setShowExitModal(false);
  };

  return (
    <Modal
      open={showExitModal}
      onClose={handleCancelNavigation}
      aria-labelledby="exit-modal-title"
      aria-describedby="exit-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: 300, sm: 400 },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <WarningAmberIcon color="warning" sx={{ fontSize: 48 }} />
          <Typography
            id="exit-modal-title"
            variant="h5"
            component="h2"
            gutterBottom
          >
            Μη αποθηκευμένες αλλαγές
          </Typography>
          <Typography
            id="exit-modal-description"
            variant="body1"
            color="text.secondary"
          >
            Έχετε αλλαγές που δεν έχουν αποθηκευτεί. Είστε σίγουροι ότι θέλετε
            να φύγετε;
          </Typography>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              onClick={handleConfirmNavigation}
              sx={{
                minWidth: 100,
                "&:hover": {
                  backgroundColor: colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              Ναι
            </Button>
            <Button
              onClick={handleCancelNavigation}
              sx={{
                minWidth: 100,
                "&:hover": {
                  backgroundColor: colors.primary,
                  opacity: 0.8,
                },
              }}
            >
              Όχι
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

ExitFormModal.propTypes = {
  isFormDirty: PropTypes.bool.isRequired,
};

export default ExitFormModal;
